// Client Portal — Email Intelligence eval-engine settings (Phase 4).
// Port of the approved mockup client-portal/email-settings-admin.html to the
// portal's Tailwind + orange-accent conventions. Two tabs: eval inputs
// (dimension cards) and routing rules (ordered WHEN/THEN, AND-chains only),
// with a dry-run strip that replays stored scores under the unpublished draft.
// The facility context replaces the mockup's tenant selector — the portal is
// already tenant-scoped.
import { useCallback, useEffect, useMemo, useState } from "react";
import { emailIntelFetch } from "../lib/api";
import { WizardShell } from "../components/WizardShell";

const DIMENSIONS = [
  "intent",
  "coverage",
  "urgency",
  "sentiment",
  "self_eval",
  "language",
];

const DIM_META = {
  intent: { name: "Intent classification", sub: "Labels the email's purpose" },
  coverage: { name: "KB coverage", sub: "Can we answer this from the KB?" },
  urgency: { name: "Urgency", sub: "Priority + churn-risk signal" },
  sentiment: { name: "Sentiment", sub: "Tone of the sender" },
  self_eval: {
    name: "Draft self-eval",
    sub: "Grades the draft before a human sees it",
  },
  language: { name: "Language detection", sub: "Route non-English to a human" },
};

const DIM_DEFAULTS = (namespace) => ({
  intent: {
    enabled: true,
    model: "gpt-4o-mini",
    labels: ["question"],
    instructions: "",
  },
  coverage: { enabled: true, namespace, top_k: 5, threshold: 0.55 },
  urgency: { enabled: true, model: "gpt-4o-mini", signals: [] },
  sentiment: { enabled: true, model: "gpt-4o-mini" },
  self_eval: {
    enabled: true,
    model: "gpt-4o-mini",
    rubric: ["grounded in KB sources"],
    min_queue_score: 0.7,
  },
  language: { enabled: true, model: "gpt-4o-mini" },
});

const ACTIONS = {
  auto_draft: { label: "Auto-draft", cls: "bg-green-100 text-green-700" },
  escalate: { label: "Escalate", cls: "bg-red-100 text-red-700" },
  hold_teach: { label: "Hold & teach", cls: "bg-amber-100 text-amber-700" },
  auto_archive: { label: "Auto-archive", cls: "bg-slate-200 text-slate-600" },
  human_no_draft: {
    label: "Human, no draft",
    cls: "bg-amber-100 text-amber-700",
  },
};

const OPS = [">=", "<=", ">", "<", "==", "!="];
const NUMERIC_OPS = new Set([">=", "<=", ">", "<"]);

const newRuleId = () =>
  window.crypto?.randomUUID
    ? `rule-${window.crypto.randomUUID().slice(0, 8)}`
    : `rule-${Math.random().toString(36).slice(2, 10)}`;

// Mirrors app/eval/config_schema.py — catch what the editor's constraints can't
// (e.g. a rule referencing a dimension that was later disabled).
function validateClient(payload) {
  const errors = [];
  if (!payload) return ["No configuration loaded."];
  const enabled = new Set(
    DIMENSIONS.filter((d) => payload.dimensions?.[d]?.enabled),
  );
  const seen = new Set();
  (payload.rules || []).forEach((rule, i) => {
    const tag = rule.name || rule.id || `rule ${i + 1}`;
    if (seen.has(rule.id)) errors.push(`Duplicate rule id on "${tag}".`);
    seen.add(rule.id);
    if (!rule.name) errors.push(`Rule ${i + 1} needs a name.`);
    if (!(rule.conditions || []).length)
      errors.push(`"${tag}" needs at least one condition.`);
    (rule.conditions || []).forEach((c) => {
      if (!enabled.has(c.dimension))
        errors.push(`"${tag}" references disabled dimension "${c.dimension}".`);
      if (NUMERIC_OPS.has(c.op) && typeof c.value !== "number")
        errors.push(`"${tag}": ${c.op} needs a numeric value.`);
    });
    if (!ACTIONS[rule.action]) errors.push(`"${tag}" has an unknown action.`);
  });
  if (!ACTIONS[payload.fallthrough?.action])
    errors.push("Fallthrough needs a valid action.");
  return errors;
}

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange(!on);
      }}
      className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${on ? "bg-orange-500" : "bg-slate-300"}`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`}
      />
    </button>
  );
}

function ChipList({ items, onChange, addLabel }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const commit = () => {
    const v = text.trim();
    if (v && !items.includes(v)) onChange([...items, v]);
    setText("");
    setAdding(false);
  };
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {items.map((it) => (
        <span
          key={it}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700"
        >
          {it}
          <button
            type="button"
            className="text-slate-400 hover:text-red-600"
            onClick={() => onChange(items.filter((x) => x !== it))}
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setAdding(false);
          }}
          className="text-xs px-2 py-1 border border-orange-300 rounded-full outline-none w-36"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-xs font-semibold text-orange-600 hover:text-orange-700 px-1"
        >
          {addLabel}
        </button>
      )}
    </div>
  );
}

const inputCls =
  "w-full text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100";
const fieldLabelCls = "block text-xs font-semibold text-slate-600 mb-1";

function Field({ label, hint, children }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className={fieldLabelCls}>{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function DimCard({ dim, cfg, namespace, onChange }) {
  const [expanded, setExpanded] = useState(dim === "intent");
  const meta = DIM_META[dim];
  const enabled = !!cfg?.enabled;
  const set = (patch) => onChange({ ...cfg, ...patch });
  const modelTag =
    dim === "coverage" ? "retrieval + score" : cfg?.model || "off";

  return (
    <div
      className={`border border-slate-200 rounded-lg mb-2.5 overflow-hidden ${enabled ? "" : "opacity-60"}`}
    >
      <div
        className="flex items-center gap-3 px-3.5 py-3 bg-slate-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <Toggle
          on={enabled}
          onChange={(on) =>
            onChange(
              cfg
                ? { ...cfg, enabled: on }
                : { ...DIM_DEFAULTS(namespace)[dim], enabled: on },
            )
          }
        />
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800">{meta.name}</p>
          <p className="text-[11px] text-slate-400">{meta.sub}</p>
        </div>
        <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-500">
          {enabled ? modelTag : "off"}
        </span>
        <span
          className={`text-xs text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
        >
          ▶
        </span>
      </div>
      {expanded && enabled && cfg && (
        <div className="p-3.5">
          {dim !== "coverage" && (
            <Field label="Model">
              <input
                className={`${inputCls} font-mono`}
                value={cfg.model}
                onChange={(e) => set({ model: e.target.value })}
              />
            </Field>
          )}
          {dim === "intent" && (
            <>
              <Field
                label="Label set"
                hint="Fixed set the classifier must choose from. Keep it small and mutually exclusive."
              >
                <ChipList
                  items={cfg.labels || []}
                  onChange={(labels) => set({ labels })}
                  addLabel="+ label"
                />
              </Field>
              <Field label="Classifier instructions">
                <textarea
                  className={`${inputCls} font-mono text-xs min-h-[70px]`}
                  value={cfg.instructions || ""}
                  onChange={(e) => set({ instructions: e.target.value })}
                />
              </Field>
            </>
          )}
          {dim === "coverage" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Retrieval top-k">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    className={inputCls}
                    value={cfg.top_k}
                    onChange={(e) =>
                      set({ top_k: parseInt(e.target.value, 10) || 1 })
                    }
                  />
                </Field>
                <Field label="Coverage threshold" hint="Below this = KB gap.">
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    className={inputCls}
                    value={cfg.threshold}
                    onChange={(e) =>
                      set({ threshold: parseFloat(e.target.value) || 0 })
                    }
                  />
                </Field>
              </div>
              <Field label="Namespace">
                <input
                  className={`${inputCls} font-mono`}
                  value={cfg.namespace}
                  onChange={(e) => set({ namespace: e.target.value })}
                />
              </Field>
            </>
          )}
          {dim === "urgency" && (
            <Field label="Signals to weigh">
              <ChipList
                items={cfg.signals || []}
                onChange={(signals) => set({ signals })}
                addLabel="+ signal"
              />
            </Field>
          )}
          {dim === "self_eval" && (
            <>
              <Field label="Grading rubric">
                <ChipList
                  items={cfg.rubric || []}
                  onChange={(rubric) => set({ rubric })}
                  addLabel="+ criterion"
                />
              </Field>
              <Field
                label="Min score to reach the review queue"
                hint="Below this, the draft is regenerated once, then routed to a human without a draft."
              >
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  className={inputCls}
                  value={cfg.min_queue_score}
                  onChange={(e) =>
                    set({ min_queue_score: parseFloat(e.target.value) || 0 })
                  }
                />
              </Field>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CondPill({ cond }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 mx-0.5 text-xs">
      <span className="font-semibold text-slate-700">
        {DIM_META[cond.dimension]?.name || cond.dimension}
      </span>
      <span className="font-mono text-slate-400">{cond.op}</span>
      <span className="font-bold text-orange-700 bg-orange-50 rounded-full px-2 py-px">
        {String(cond.value)}
      </span>
    </span>
  );
}

function RuleEditor({ rule, enabledDims, onSave, onCancel }) {
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(rule)));
  const setCond = (i, patch) => {
    const conditions = draft.conditions.map((c, j) =>
      j === i ? { ...c, ...patch } : c,
    );
    setDraft({ ...draft, conditions });
  };
  const parseValue = (raw, op) => {
    if (NUMERIC_OPS.has(op)) return parseFloat(raw) || 0;
    const num = parseFloat(raw);
    return !Number.isNaN(num) && String(num) === raw.trim() ? num : raw;
  };
  return (
    <div className="p-3.5 border-t border-slate-100 bg-orange-50/40">
      <Field label="Rule name">
        <input
          className={inputCls}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </Field>
      <Field label="WHEN (all conditions must match — AND only; use rule order for OR)">
        {draft.conditions.map((c, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <select
              className={`${inputCls} w-44`}
              value={c.dimension}
              onChange={(e) => setCond(i, { dimension: e.target.value })}
            >
              {enabledDims.map((d) => (
                <option key={d} value={d}>
                  {DIM_META[d].name}
                </option>
              ))}
              {!enabledDims.includes(c.dimension) && (
                <option value={c.dimension}>{c.dimension} (disabled)</option>
              )}
            </select>
            <select
              className={`${inputCls} w-20 font-mono`}
              value={c.op}
              onChange={(e) =>
                setCond(i, {
                  op: e.target.value,
                  value: parseValue(String(c.value), e.target.value),
                })
              }
            >
              {OPS.map((op) => (
                <option key={op}>{op}</option>
              ))}
            </select>
            <input
              className={`${inputCls} w-32`}
              value={String(c.value)}
              onChange={(e) =>
                setCond(i, { value: parseValue(e.target.value, c.op) })
              }
            />
            <button
              type="button"
              className="text-slate-400 hover:text-red-600 text-sm px-1"
              onClick={() =>
                setDraft({
                  ...draft,
                  conditions: draft.conditions.filter((_, j) => j !== i),
                })
              }
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs font-semibold text-orange-600 hover:text-orange-700"
          onClick={() =>
            setDraft({
              ...draft,
              conditions: [
                ...draft.conditions,
                { dimension: enabledDims[0], op: ">=", value: 0.5 },
              ],
            })
          }
        >
          + AND condition
        </button>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="THEN action">
          <select
            className={inputCls}
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
          >
            {Object.entries(ACTIONS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Sub-action note (optional)">
          <input
            className={inputCls}
            value={draft.sub_action || ""}
            onChange={(e) =>
              setDraft({ ...draft, sub_action: e.target.value || null })
            }
          />
        </Field>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-50"
          disabled={!draft.name || !draft.conditions.length}
          onClick={() => onSave(draft)}
        >
          Apply
        </button>
        <button
          type="button"
          className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function EmailIntelSettings({ facility }) {
  const facilityId = facility.facility_id;
  const [payload, setPayload] = useState(null);
  const [baseline, setBaseline] = useState(null); // last loaded/saved state, for dirty check
  const [namespace, setNamespace] = useState("");
  const [liveVersion, setLiveVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editingRule, setEditingRule] = useState(null); // rule index or "new"
  const [dryRun, setDryRun] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);

  const dirty = useMemo(
    () => payload && JSON.stringify(payload) !== JSON.stringify(baseline),
    [payload, baseline],
  );
  const clientErrors = useMemo(
    () => (payload ? validateClient(payload) : []),
    [payload],
  );
  const enabledDims = useMemo(
    () =>
      payload ? DIMENSIONS.filter((d) => payload.dimensions?.[d]?.enabled) : [],
    [payload],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await emailIntelFetch(
        `/eval-config?facility_id=${facilityId}`,
      );
      setNamespace(data.namespace);
      setLiveVersion(data.published?.version ?? null);
      const current = data.draft?.payload ?? data.published?.payload ?? null;
      setPayload(current);
      setBaseline(JSON.parse(JSON.stringify(current)));
      const vs = await emailIntelFetch(
        `/eval-config/versions?facility_id=${facilityId}`,
      );
      setVersions(vs.versions || []);
    } catch (e) {
      setError("Could not load the eval configuration.");
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const startFromTemplate = async () => {
    setBusy(true);
    try {
      const data = await emailIntelFetch(
        `/eval-config/default?facility_id=${facilityId}`,
      );
      setPayload(data.payload);
    } catch (e) {
      setError("Could not load the starter template.");
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      await emailIntelFetch(`/eval-config/draft`, {
        method: "PUT",
        body: JSON.stringify({ facility_id: facilityId, payload }),
      });
      setBaseline(JSON.parse(JSON.stringify(payload)));
      setNotice("Draft saved.");
      const vs = await emailIntelFetch(
        `/eval-config/versions?facility_id=${facilityId}`,
      );
      setVersions(vs.versions || []);
    } catch (e) {
      setError("Save failed — the configuration was rejected by the server.");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (
      !window.confirm(
        "Publish this configuration? It becomes the live version governing how this facility's email routes.",
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      if (dirty) {
        await emailIntelFetch(`/eval-config/draft`, {
          method: "PUT",
          body: JSON.stringify({ facility_id: facilityId, payload }),
        });
      }
      const res = await emailIntelFetch(`/eval-config/publish`, {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId }),
      });
      setNotice(`Published v${res.version} — now live for this facility.`);
      await load();
    } catch (e) {
      setError("Publish failed.");
    } finally {
      setBusy(false);
    }
  };

  const restoreVersion = async (version) => {
    setBusy(true);
    try {
      const row = await emailIntelFetch(
        `/eval-config/version?facility_id=${facilityId}&version=${version}`,
      );
      setPayload(row.payload);
      setNotice(
        `Loaded v${version} into the editor — save or publish to apply.`,
      );
    } catch (e) {
      setError("Could not load that version.");
    } finally {
      setBusy(false);
    }
  };

  const runDryRun = async () => {
    setDryRunning(true);
    setError(null);
    try {
      const res = await emailIntelFetch(`/eval-config/dry-run`, {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId, payload, last_n: 10 }),
      });
      setDryRun(res.results || []);
    } catch (e) {
      setError("Dry-run failed — fix any validation errors first.");
    } finally {
      setDryRunning(false);
    }
  };

  const setDim = (dim, cfg) =>
    setPayload({
      ...payload,
      dimensions: { ...payload.dimensions, [dim]: cfg },
    });

  const moveRule = (i, delta) => {
    const rules = [...payload.rules];
    const j = i + delta;
    if (j < 0 || j >= rules.length) return;
    [rules[i], rules[j]] = [rules[j], rules[i]];
    setPayload({ ...payload, rules });
  };

  if (loading)
    return (
      <div className="text-center py-12 text-slate-400">
        Loading eval settings…
      </div>
    );

  if (!payload)
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <h3 className="font-semibold text-slate-700 mb-2">
          Eval engine not configured
        </h3>
        <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
          Start from the recommended template — intent, KB coverage, urgency and
          draft self-eval, with four starter routing rules — then tune it for
          this facility.
        </p>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <button
          onClick={startFromTemplate}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50"
        >
          Start from template
        </button>
      </div>
    );

  const steps = [
    {
      key: "dimensions",
      title: "Dimensions",
      content: (
        <>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 mb-1">
            Evaluation dimensions
          </p>
          <p className="text-xs text-slate-500 mb-4">
            Each enabled dimension runs on every inbound email and produces a
            score the routing rules can reference. Disable a dimension to skip
            it (and stop paying for the call).
          </p>
          <div className="flex items-center gap-2.5 mb-4 px-3 py-2.5 rounded-lg bg-green-50 border border-green-200 text-xs text-green-800">
            <span>●</span>
            <span>
              KB retrieval namespace for this facility:{" "}
              <b className="font-mono">{namespace}</b>
            </span>
          </div>
          {DIMENSIONS.map((dim) => (
            <DimCard
              key={dim}
              dim={dim}
              cfg={payload.dimensions?.[dim]}
              namespace={namespace}
              onChange={(cfg) => setDim(dim, cfg)}
            />
          ))}
        </>
      ),
    },
    {
      key: "drafting",
      title: "Drafting & Budget",
      content: (
        <>
          <div className="border border-slate-200 rounded-lg p-3.5">
            <p className="text-sm font-bold text-slate-800 mb-1">Cost guard</p>
            <Field
              label="Daily eval budget cap (emails/day)"
              hint="Past the cap, remaining mail routes to a human with no model calls. Leave blank for no cap."
            >
              <input
                type="number"
                min="1"
                className={`${inputCls} w-44`}
                value={payload.budget?.daily_eval_cap ?? ""}
                onChange={(e) =>
                  setPayload({
                    ...payload,
                    budget: {
                      daily_eval_cap: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    },
                  })
                }
              />
            </Field>
          </div>
          <div className="border border-slate-200 rounded-lg mt-2.5 p-3.5">
            <p className="text-sm font-bold text-slate-800 mb-1">
              Draft generation
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Model">
                <input
                  className={`${inputCls} font-mono`}
                  value={payload.draft?.model || "gpt-4o-mini"}
                  onChange={(e) =>
                    setPayload({
                      ...payload,
                      draft: {
                        ...(payload.draft || {}),
                        model: e.target.value,
                      },
                    })
                  }
                />
              </Field>
              <Field label="Tenant instructions (tone, sign-off)">
                <input
                  className={inputCls}
                  value={payload.draft?.instructions || ""}
                  onChange={(e) =>
                    setPayload({
                      ...payload,
                      draft: {
                        ...(payload.draft || {}),
                        model: payload.draft?.model || "gpt-4o-mini",
                        instructions: e.target.value,
                      },
                    })
                  }
                />
              </Field>
            </div>
          </div>
        </>
      ),
    },
    {
      key: "rules",
      title: "Routing Rules",
      content: (
        <>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 mb-1">
            Routing rules
          </p>
          <p className="text-xs text-slate-500 mb-4">
            Rules evaluate top to bottom; the first match wins. Each rule reads
            the scores from the eval tab — thresholds become actions.
          </p>
          {(payload.rules || []).map((rule, i) => (
            <div
              key={rule.id}
              className="border border-slate-200 rounded-lg mb-2.5 overflow-hidden"
            >
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-slate-50">
                <span className="w-5 h-5 rounded bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="flex-1 text-sm font-bold text-slate-800">
                  {rule.name}
                </span>
                <span
                  className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${ACTIONS[rule.action]?.cls || ""}`}
                >
                  {ACTIONS[rule.action]?.label || rule.action}
                </span>
                <span className="flex flex-col -my-1">
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-700 text-[10px] leading-3 disabled:opacity-30"
                    disabled={i === 0}
                    onClick={() => moveRule(i, -1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-700 text-[10px] leading-3 disabled:opacity-30"
                    disabled={i === payload.rules.length - 1}
                    onClick={() => moveRule(i, 1)}
                  >
                    ▼
                  </button>
                </span>
              </div>
              <div className="px-3.5 py-3 text-xs text-slate-500 leading-7">
                WHEN{" "}
                {rule.conditions.map((c, j) => (
                  <span key={j}>
                    {j > 0 && (
                      <span className="font-mono text-[10px] text-slate-400 mx-1">
                        AND
                      </span>
                    )}
                    <CondPill cond={c} />
                  </span>
                ))}
                <span className="font-mono text-slate-400 mx-1.5">→</span>
                <b className="text-slate-700">
                  {ACTIONS[rule.action]?.label || rule.action}
                </b>
                {rule.sub_action && (
                  <span className="text-slate-400"> · {rule.sub_action}</span>
                )}
              </div>
              {editingRule === i ? (
                <RuleEditor
                  rule={rule}
                  enabledDims={enabledDims.filter((d) => d !== "language")}
                  onSave={(updated) => {
                    const rules = payload.rules.map((r, j) =>
                      j === i ? updated : r,
                    );
                    setPayload({ ...payload, rules });
                    setEditingRule(null);
                  }}
                  onCancel={() => setEditingRule(null)}
                />
              ) : (
                <div className="flex gap-3 px-3.5 py-2 border-t border-slate-100">
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-400 hover:text-slate-700"
                    onClick={() => setEditingRule(i)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-400 hover:text-slate-700"
                    onClick={() => {
                      const copy = {
                        ...JSON.parse(JSON.stringify(rule)),
                        id: newRuleId(),
                        name: `${rule.name} (copy)`,
                      };
                      const rules = [...payload.rules];
                      rules.splice(i + 1, 0, copy);
                      setPayload({ ...payload, rules });
                    }}
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-400 hover:text-red-600"
                    onClick={() =>
                      setPayload({
                        ...payload,
                        rules: payload.rules.filter((_, j) => j !== i),
                      })
                    }
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}

          {editingRule === "new" ? (
            <div className="border border-slate-200 rounded-lg mb-2.5">
              <RuleEditor
                rule={{
                  id: newRuleId(),
                  name: "",
                  joiner: "AND",
                  action: "escalate",
                  conditions: [
                    {
                      dimension: enabledDims[0] || "urgency",
                      op: ">=",
                      value: 0.5,
                    },
                  ],
                }}
                enabledDims={enabledDims.filter((d) => d !== "language")}
                onSave={(rule) => {
                  setPayload({
                    ...payload,
                    rules: [...(payload.rules || []), rule],
                  });
                  setEditingRule(null);
                }}
                onCancel={() => setEditingRule(null)}
              />
            </div>
          ) : (
            <button
              type="button"
              className="text-sm font-semibold text-orange-600 hover:text-orange-700 mb-2"
              onClick={() => setEditingRule("new")}
            >
              + Add rule
            </button>
          )}

          <div className="flex items-center gap-3 px-3.5 py-3 border border-dashed border-slate-300 rounded-lg bg-slate-50 text-xs text-slate-500 mt-1">
            <span className="font-mono text-[10px] font-semibold text-slate-400">
              FALLTHROUGH
            </span>
            Anything matching no rule above →
            <select
              className="text-xs px-2 py-1 border border-slate-200 rounded-lg ml-auto"
              value={payload.fallthrough?.action || "human_no_draft"}
              onChange={(e) =>
                setPayload({
                  ...payload,
                  fallthrough: {
                    ...(payload.fallthrough || {}),
                    action: e.target.value,
                  },
                })
              }
            >
              {Object.entries(ACTIONS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
        </>
      ),
    },
    {
      key: "review",
      title: "Review & Publish",
      nextDisabled: clientErrors.length > 0,
      content: (
        <>
          {clientErrors.length > 0 ? (
            <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3.5">
              <p className="text-xs font-semibold text-red-700 mb-1.5">
                Fix these before publishing:
              </p>
              <ul className="text-xs text-red-600 space-y-1 list-disc pl-4">
                {clientErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="mb-4 border border-green-200 bg-green-50 rounded-lg p-3.5 text-xs text-green-800">
              Configuration is valid.{" "}
              {liveVersion
                ? `Publishing replaces live v${liveVersion}.`
                : "Publishing makes this facility's first live version."}
            </div>
          )}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 border-b border-slate-100">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                Dry-run · how recent emails would route under this draft
              </span>
              <button
                type="button"
                onClick={runDryRun}
                disabled={dryRunning || clientErrors.length > 0}
                className="text-xs font-semibold px-3 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                {dryRunning ? "Running…" : "Run dry-run"}
              </button>
            </div>
            {dryRun === null ? (
              <p className="px-3.5 py-3 text-xs text-slate-400">
                Replays the last 10 evaluated emails' stored scores under the
                unpublished draft — no model calls, nothing recorded.
              </p>
            ) : dryRun.length === 0 ? (
              <p className="px-3.5 py-3 text-xs text-slate-400">
                No evaluated emails yet for this facility.
              </p>
            ) : (
              dryRun.map((r) => {
                const ruleName =
                  payload.rules.find((x) => x.id === r.matched_rule_id)?.name ||
                  "Fallthrough";
                return (
                  <div
                    key={r.email_id}
                    className="flex items-center gap-3 px-3.5 py-2.5 border-b border-slate-100 last:border-b-0 text-xs"
                  >
                    <span className="font-mono text-slate-400 truncate max-w-[180px]">
                      {r.email_id}
                    </span>
                    <span className="flex-1 text-slate-500 truncate">
                      {ruleName}
                    </span>
                    <span
                      className={`font-bold px-2.5 py-0.5 rounded-full ${ACTIONS[r.action]?.cls || ""}`}
                    >
                      {ACTIONS[r.action]?.label || r.action}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
        <div>
          <h3 className="font-semibold text-slate-700">Eval engine settings</h3>
          <p className="text-xs text-slate-400">
            {liveVersion
              ? `Live: v${liveVersion}`
              : "Not published yet — nothing is live"}
          </p>
        </div>
        {versions.length > 0 && (
          <select
            className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg text-slate-600"
            value=""
            onChange={(e) =>
              e.target.value && restoreVersion(Number(e.target.value))
            }
          >
            <option value="">Version history…</option>
            {versions.map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version} · {v.status}
                {v.version === liveVersion ? " (live)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="p-5">
        <WizardShell
          accent="orange"
          steps={steps}
          onStepChange={() => {
            if (dirty && clientErrors.length === 0) saveDraft();
          }}
          onFinish={publish}
          finishLabel="Publish to Facility"
          busy={busy}
        />
      </div>

      <div className="flex items-center gap-2.5 px-5 py-3.5 border-t border-slate-100 bg-slate-50/60">
        {clientErrors.length > 0 ? (
          <span className="text-xs text-red-600 font-medium">
            {clientErrors[0]}
          </span>
        ) : dirty ? (
          <span className="text-xs text-amber-600 font-semibold">
            ● Unsaved changes
          </span>
        ) : notice ? (
          <span className="text-xs text-green-700 font-medium">{notice}</span>
        ) : null}
        {error && (
          <span className="text-xs text-red-600 font-medium">{error}</span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={load}
          disabled={busy || !dirty}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40"
        >
          Revert
        </button>
        <button
          type="button"
          onClick={saveDraft}
          disabled={busy || !dirty || clientErrors.length > 0}
          className="px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          Save as draft
        </button>
      </div>
    </div>
  );
}
